import { parse } from "mathjs";

export const closeEquationNumber = (a, b) => Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
const constant = value => new Map([["0,0", value]]);
export const unwrapEquationNode = node => node.isParenthesisNode ? unwrapEquationNode(node.content) : node;

function normalizeEquation(input) {
  if (typeof input !== "string" || input.length > 600) throw new Error("Invalid equation");
  let text = input.trim().replace(/\\left|\\right/g, "")
    .replace(/[−–]/g, "-").replace(/²/g, "^2").replace(/\*\*/g, "^")
    .replace(/\\(?:cdot|times)|[×·]/g, "*").replace(/\\[,;! ]/g, "")
    .replace(/\{,\}/g, ".").replace(/,/g, ".");
  // A mixed number denotes addition, not implicit multiplication.
  text = text.replace(/(\d+)\s+(\d+)\s*\/\s*(\d+)/g, "($1+$2/$3)")
    .replace(/(\d+)\s*\\(?:dfrac|tfrac|frac)\{(\d+)\}\{(\d+)\}/g, "($1+$2/$3)");
  text = text.replace(/(\d*)([½¼¾])/g, (_, whole, fraction) => {
    const value = { "½": "1/2", "¼": "1/4", "¾": "3/4" }[fraction];
    return whole ? `(${whole}+${value})` : `(${value})`;
  });
  for (let i = 0; i < 12 && /\\(?:dfrac|tfrac|frac|sqrt)/.test(text); i += 1) {
    const previous = text;
    text = text.replace(/\\(?:dfrac|tfrac|frac)\{([^{}]+)\}\{([^{}]+)\}/g, "(($1)/($2))")
      .replace(/\\sqrt\{([^{}]+)\}/g, "sqrt($1)");
    if (previous === text) break;
  }
  return text.replace(/\{/g, "(").replace(/\}/g, ")")
    .replace(/(\d|\))(?=[xy]\b)/g, "$1*");
}

export function addPolynomials(a, b, sign = 1) {
  const result = new Map(a);
  b.forEach((value, key) => result.set(key, (result.get(key) || 0) + sign * value));
  return result;
}

function multiply(a, b) {
  const result = new Map();
  a.forEach((left, leftKey) => b.forEach((right, rightKey) => {
    if (left === 0 || right === 0) return;
    const [lx, ly] = leftKey.split(",").map(Number);
    const [rx, ry] = rightKey.split(",").map(Number);
    if (lx + ly + rx + ry > 2) throw new Error("Not quadratic");
    const key = `${lx + rx},${ly + ry}`;
    result.set(key, (result.get(key) || 0) + left * right);
  }));
  return result;
}

export function polynomialNumber(poly) {
  if ([...poly].some(([key, value]) => key !== "0,0" && value !== 0)) throw new Error("Not constant");
  return poly.get("0,0") || 0;
}

export function equationPolynomial(root) {
  let count = 0;
  const visit = raw => {
    if (++count > 160) throw new Error("Expression too complex");
    const node = unwrapEquationNode(raw);
    let result;
    if (node.isConstantNode) result = constant(Number(node.value));
    else if (node.isSymbolNode && ["x", "y"].includes(node.name)) {
      result = new Map([[node.name === "x" ? "1,0" : "0,1", 1]]);
    } else if (node.isFunctionNode && node.fn.name === "sqrt" && node.args.length === 1) {
      result = constant(Math.sqrt(polynomialNumber(visit(node.args[0]))));
    } else if (node.isOperatorNode) {
      const args = node.args.map(visit);
      if (node.op === "+") result = args.length === 1 ? args[0] : addPolynomials(args[0], args[1]);
      else if (node.op === "-") result = args.length === 1 ? multiply(constant(-1), args[0]) : addPolynomials(args[0], args[1], -1);
      else if (node.op === "*") result = multiply(args[0], args[1]);
      else if (node.op === "/") {
        const denominator = polynomialNumber(args[1]);
        if (denominator === 0) throw new Error("Division by zero");
        result = multiply(args[0], constant(1 / denominator));
      } else if (node.op === "^") {
        const exponent = polynomialNumber(args[1]);
        if (![0, 1, 2].includes(exponent)) throw new Error("Unsupported power");
        result = exponent === 0 ? constant(1) : exponent === 1 ? args[0] : multiply(args[0], args[0]);
      }
    }
    if (!result || [...result.values()].some(value => !Number.isFinite(value) || Math.abs(value) > 1e12)) {
      throw new Error("Unsupported expression");
    }
    return result;
  };
  return visit(root);
}

export function equationParts(input) {
  const sides = normalizeEquation(input).split("=");
  if (sides.length !== 2 || sides.some(side => !side.trim())) throw new Error("Expected one equality");
  return sides.map(side => parse(side));
}

